/**
 * Reads an uploaded .zip of a static site in the browser and runs the same checks Website Runtime
 * applies (validators/static-site.validator.ts), so problems show before anything is uploaded.
 *
 * The checks run on the zip's table of contents (file names and declared sizes) BEFORE any file is
 * unpacked, so a zip that is too big or the wrong shape is refused at once instead of being unpacked
 * first. Unpacking uses fflate's synchronous API: its async API starts Web Workers from `blob:` URLs,
 * which the portal's Content-Security-Policy blocks, and a blocked worker never reports back, which
 * left the page on "Checking your files…" for ever.
 */
import { unzipSync } from "fflate";

import type { StaticFile } from "./types";

/** Website Runtime's deployment limits (RUNTIME_MAX_SOURCE_FILES / RUNTIME_MAX_SOURCE_BYTES). */
export const MAX_FILES = 2000;
export const MAX_BYTES = 20 * 1024 * 1024;
/**
 * The biggest .zip that can hold a site within MAX_BYTES: a zip adds only a few hundred bytes per file, so
 * anything larger cannot fit. Larger files are refused without being read into memory.
 */
export const MAX_ZIP_BYTES = MAX_BYTES + 1024 * 1024;

const PATH = /^[A-Za-z0-9_@][A-Za-z0-9_@.()+~ -]*(\/[A-Za-z0-9_@][A-Za-z0-9_@.()+~ -]*)*$/;
const SERVER_SIDE_EXTENSIONS = [
  ".php",
  ".phtml",
  ".asp",
  ".aspx",
  ".jsp",
  ".py",
  ".rb",
  ".pl",
  ".cgi",
  ".sh",
  ".env",
];
const SERVER_SIDE_FILES = new Set([
  "package.json",
  "server.js",
  "composer.json",
  "requirements.txt",
  ".htaccess",
]);
/** Archive clutter from macOS and Windows that is never part of a site. */
const JUNK = (path: string) =>
  path.startsWith("__MACOSX/") || /(^|\/)(\.DS_Store|Thumbs\.db|desktop\.ini)$/i.test(path);
/** Development folders that are not part of a website but often end up in a zip. */
const DEV_FOLDER = /(^|\/)(node_modules|\.git|\.svn|\.hg)\//;

export interface SiteCheck {
  label: string;
  ok: boolean;
}

export type CheckStage = "reading" | "inspecting" | "unpacking";

/** How far the check has got, for the progress bar. `fraction` runs from 0 to 1 across all stages. */
export interface CheckProgress {
  stage: CheckStage;
  fraction: number;
}

export const STAGE_LABEL: Record<CheckStage, string> = {
  reading: "Reading your file…",
  inspecting: "Checking what is inside…",
  unpacking: "Unpacking your files…",
};

export interface CheckedSite {
  fileName: string;
  /** Size of the .zip itself. */
  zipBytes: number;
  /** Files in the site, once unpacked. */
  fileCount: number;
  /** Size of the site once unpacked. */
  totalBytes: number;
  /** The unpacked files: only filled in when every check passed (nothing is unpacked otherwise). */
  files: { path: string; data: Uint8Array }[];
  checks: SiteCheck[];
  /** Plain advice for fixing what failed (or for what looks out of place), when there is any. */
  hints: string[];
  /** Why the site cannot be deployed, or null when every check passed. */
  problem: string | null;
}

/** A file in the zip: `name` is how the zip spells it, `path` is where it lands in the site. */
interface Entry {
  name: string;
  path: string;
  size: number;
}

interface Inspection {
  entries: Entry[];
  totalBytes: number;
  checks: SiteCheck[];
  hints: string[];
  problem: string | null;
}

const READ_END = 0.35;
const INSPECT_END = 0.45;
const BATCH_BYTES = 1024 * 1024;
const BATCH_FILES = 200;

const tick = () => new Promise<void>((resolve) => setTimeout(resolve, 0));
const count = (n: number) => n.toLocaleString();

function readFile(file: File, onFraction: (fraction: number) => void): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    const failed = () =>
      reject(new Error("That file could not be read. Check that it still exists, then choose it again."));
    reader.onprogress = (e) => {
      if (e.lengthComputable && e.total > 0) onFraction(e.loaded / e.total);
    };
    reader.onerror = failed;
    reader.onabort = failed;
    reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer));
    reader.readAsArrayBuffer(file);
  });
}

/** The zip's table of contents: every file's name and declared size. Nothing is unpacked. */
function listEntries(data: Uint8Array): { name: string; size: number }[] {
  const listed: { name: string; size: number }[] = [];
  try {
    unzipSync(data, {
      filter: (f) => {
        if (!f.name.endsWith("/")) listed.push({ name: f.name, size: f.originalSize });
        return false;
      },
    });
  } catch {
    throw new Error("That file is not a readable .zip");
  }
  return listed;
}

/** Runs every check on the list of files, without looking inside any of them. */
function inspect(listed: { name: string; size: number }[], storageLeft: number): Inspection {
  const kept = listed.filter((f) => !JUNK(f.name));
  // Exports often wrap everything in one folder ("my-site/index.html"): host what is inside it.
  const top = new Set(kept.map((f) => f.name.split("/")[0]));
  const [only] = [...top];
  const prefix =
    top.size === 1 &&
    only !== undefined &&
    !kept.some((f) => f.name === "index.html") &&
    kept.some((f) => f.name === `${only}/index.html`)
      ? `${only}/`
      : "";
  const entries: Entry[] = kept.map((f) => ({
    name: f.name,
    path: f.name.slice(prefix.length),
    size: f.size,
  }));
  const totalBytes = entries.reduce((n, f) => n + f.size, 0);

  const badNames = entries.filter((f) => !PATH.test(f.path) || f.path.length > 255);
  const serverCode = entries.filter((f) => {
    const name = (f.path.split("/").at(-1) ?? f.path).toLowerCase();
    return SERVER_SIDE_FILES.has(name) || SERVER_SIDE_EXTENSIONS.some((e) => name.endsWith(e));
  });
  const hasIndex = entries.some((f) => f.path === "index.html");
  const tooMany = entries.length > MAX_FILES;
  const tooBig = totalBytes > MAX_BYTES;
  const fitsStorage = totalBytes <= storageLeft;
  const limitMb = MAX_BYTES / 2 ** 20;

  const checks: SiteCheck[] = [
    {
      label: hasIndex ? "index.html found at the top level" : "No index.html at the top level",
      ok: hasIndex,
    },
    {
      label: serverCode[0]
        ? `${serverCode[0].path} is server-side code${serverCode.length > 1 ? ` (and ${count(serverCode.length - 1)} more)` : ""}`
        : "Only static files, no server-side code",
      ok: serverCode.length === 0,
    },
    {
      label: tooMany
        ? `Too many files: ${count(entries.length)} (the limit is ${count(MAX_FILES)})`
        : `${count(entries.length)} files, within the ${count(MAX_FILES)} file limit`,
      ok: !tooMany,
    },
    {
      label: tooBig
        ? `Too large: ${mb(totalBytes)} once unpacked (the limit is ${limitMb} MB)`
        : `${mb(totalBytes)} once unpacked, within the ${limitMb} MB limit`,
      ok: !tooBig,
    },
    {
      label: `${fitsStorage ? "Fits your storage" : "Not enough storage"}: uses ${mb(totalBytes)} of ${mb(Math.max(storageLeft, 0))} left`,
      ok: fitsStorage,
    },
  ];
  if (badNames[0])
    checks.splice(1, 0, {
      label: `“${badNames[0].path}” has characters a web address can’t use${badNames.length > 1 ? ` (and ${count(badNames.length - 1)} more files)` : ""}`,
      ok: false,
    });

  const hints: string[] = [];
  const clutter = entries.filter((f) => DEV_FOLDER.test(f.name));
  if (clutter.length > 0) {
    const folders = [...new Set(clutter.map((f) => DEV_FOLDER.exec(f.name)?.[2]))].sort().join(", ");
    hints.push(
      `${count(clutter.length)} of the ${count(entries.length)} files (${mb(clutter.reduce((n, f) => n + f.size, 0))}) are in ${folders}. These are development files, not part of a website: leave them out when you make the .zip.`,
    );
  }
  if (!hasIndex) {
    const nested = entries
      .filter((f) => f.path.endsWith("/index.html") && !DEV_FOLDER.test(f.name))
      .sort((a, b) => a.path.split("/").length - b.path.split("/").length)[0];
    if (nested) {
      const folder = nested.path.slice(0, -"index.html".length);
      hints.push(
        `We found an index.html inside “${folder}”. Make the .zip from the files inside that folder, not from the folder or the project around it.`,
      );
    } else if (entries.length > 0) {
      hints.push("Your site’s home page must be a file called index.html in the top level of the .zip.");
    }
  }

  const problem = entries.length === 0 ? "The .zip is empty" : (checks.find((c) => !c.ok)?.label ?? null);
  return { entries, totalBytes, checks, hints, problem };
}

/** Unpacks the files in small batches, so the page can show progress and stay responsive. */
async function unpack(
  data: Uint8Array,
  entries: Entry[],
  onProgress: (p: CheckProgress) => void,
): Promise<{ path: string; data: Uint8Array }[]> {
  const total = entries.reduce((n, f) => n + f.size, 0);
  const out: { path: string; data: Uint8Array }[] = [];
  let done = 0;
  for (let i = 0; i < entries.length;) {
    const batch: Entry[] = [];
    let bytes = 0;
    while (i < entries.length && batch.length < BATCH_FILES && (batch.length === 0 || bytes < BATCH_BYTES)) {
      const next = entries[i++] as Entry;
      batch.push(next);
      bytes += next.size;
    }
    const names = new Set(batch.map((f) => f.name));
    let unpacked: Record<string, Uint8Array>;
    try {
      unpacked = unzipSync(data, { filter: (f) => names.has(f.name) });
    } catch {
      throw new Error("The .zip could not be unpacked. It may be damaged or protected with a password.");
    }
    for (const f of batch) out.push({ path: f.path, data: unpacked[f.name] ?? new Uint8Array() });
    done += bytes;
    onProgress({
      stage: "unpacking",
      fraction: INSPECT_END + (1 - INSPECT_END) * (total > 0 ? done / total : 1),
    });
    await tick();
  }
  return out;
}

/** A site that failed its checks: nothing was unpacked, so `files` is empty and nothing is uploaded. */
function refused(fileName: string, zipBytes: number, found: Inspection): CheckedSite {
  return {
    fileName,
    zipBytes,
    fileCount: found.entries.length,
    totalBytes: found.totalBytes,
    files: [],
    checks: found.checks,
    hints: found.hints,
    problem: found.problem,
  };
}

/**
 * Checks a site .zip. `storageLeft` is the plan's remaining storage in bytes. Rejects (with a message
 * for the customer) when the file cannot be read as a .zip at all; a readable .zip that breaks a rule
 * resolves with `problem` set and `files` empty.
 */
export async function checkSiteZip(
  file: File,
  storageLeft: number,
  onProgress: (p: CheckProgress) => void = () => undefined,
): Promise<CheckedSite> {
  if (file.size > MAX_ZIP_BYTES) {
    const label = `This .zip is ${mb(file.size)}. A site can be at most ${MAX_BYTES / 2 ** 20} MB once unpacked.`;
    return {
      fileName: file.name,
      zipBytes: file.size,
      fileCount: 0,
      totalBytes: 0,
      files: [],
      checks: [{ label, ok: false }],
      hints: [
        "Leave out node_modules, .git, source files and large originals (for example uncompressed videos or photos) when you make the .zip.",
      ],
      problem: label,
    };
  }

  onProgress({ stage: "reading", fraction: 0 });
  const data = await readFile(file, (f) => onProgress({ stage: "reading", fraction: f * READ_END }));
  onProgress({ stage: "inspecting", fraction: READ_END });
  await tick();

  const found = inspect(listEntries(data), storageLeft);
  if (found.problem !== null) {
    onProgress({ stage: "inspecting", fraction: 1 });
    return refused(file.name, file.size, found);
  }
  onProgress({ stage: "unpacking", fraction: INSPECT_END });
  await tick();

  const files = await unpack(data, found.entries, onProgress);
  // The sizes in a .zip are only what it claims: check again against what was really unpacked.
  const real = inspect(
    files.map((f, i) => ({ name: (found.entries[i] as Entry).name, size: f.data.length })),
    storageLeft,
  );
  if (real.problem !== null) return refused(file.name, file.size, real);
  onProgress({ stage: "unpacking", fraction: 1 });
  return {
    fileName: file.name,
    zipBytes: file.size,
    fileCount: real.entries.length,
    totalBytes: real.totalBytes,
    files,
    checks: real.checks,
    hints: real.hints,
    problem: null,
  };
}

/** The upload body for the runtime. Refuses a site that did not pass its checks. */
export function toStaticFiles(site: CheckedSite): StaticFile[] {
  if (site.problem !== null) throw new Error(site.problem);
  return site.files.map((f) => ({ path: f.path, content: base64(f.data), encoding: "base64" }));
}

/** "bloom-events-site.zip" → "Bloom events site". */
export function siteNameFromFile(fileName: string): string {
  const base =
    fileName
      .replace(/\.zip$/i, "")
      .replace(/[-_.]+/g, " ")
      .trim() || "External site";
  return (base.charAt(0).toUpperCase() + base.slice(1)).slice(0, 60);
}

export function mb(bytes: number): string {
  if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
  if (bytes < 1024 ** 2) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  const v = bytes / 1024 ** 2;
  return `${v < 10 ? v.toFixed(1) : Math.round(v)} MB`;
}

function base64(data: Uint8Array): string {
  let s = "";
  for (let i = 0; i < data.length; i += 0x8000) s += String.fromCharCode(...data.subarray(i, i + 0x8000));
  return btoa(s);
}
